import { useState, type ReactNode } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";
import type { Goods } from "@libs/buy-on-behalf-client/goods";
import { goodsWords } from "@libs/buy-on-behalf-client/goods";
import { MUSIC_ACCESS_FLOW_VISUAL_RECIPE as recipe } from "../music-access-flow/recipe";
import {
  detailsReady,
  formatMoney,
  MEMBER_PRICE,
  formatWhen,
  phoneReady,
  orderLimitState,
  tierAvailability,
  tierForFan,
  tierLabel,
  tierMax,
  type EventDetailRule,
  type EventDetailRules,
  type EventLine,
  type EventOrderLimits,
  type EventTier,
} from "./model";

/* The native twin of the web ticket screen: same recipe, same copy, same
 * layout. Hosts provide the screen chrome (handle, close, safe areas) and
 * the transport — on native the checkout itself is the hosted page in an
 * in-app browser, so selecting tickets leads straight there.
 *
 * `variant="light"` is the Select tickets sheet (LIS-846): white surface,
 * grey tier cards, round steppers, a blue primary button, and free tiers
 * offered as a "Register" link until one is picked. `dark` (default) is the
 * original full-screen look. */

export type EventTicketsVariant = "dark" | "light";

export const LIGHT_TICKET_COLORS = {
  text: "#1d1d1f",
  secondary: "#6e6e73",
  card: "#f5f5f7",
  surface: "#ffffff",
  border: "#d2d2d7",
  primary: "#0082ec",
  onPrimary: "#ffffff",
} as const;
const light = LIGHT_TICKET_COLORS;

function Glyph({
  d,
  size = 14,
  color = recipe.colors.white,
}: {
  d: string;
  size?: number;
  color?: string;
}) {
  return (
    <Svg height={size} viewBox="0 0 24 24" width={size}>
      <Path
        d={d}
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </Svg>
  );
}

export function QuantityStepper({
  value,
  min = 0,
  max = 100,
  disabled,
  onChange,
  variant = "dark",
}: {
  value: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onChange(next: number): void;
  variant?: EventTicketsVariant;
}) {
  const isLight = variant === "light";
  return (
    <View
      accessibilityLabel="Quantity"
      accessibilityRole="adjustable"
      style={styles.stepper}
    >
      <Pressable
        accessibilityLabel="Fewer"
        accessibilityRole="button"
        disabled={disabled || value <= min}
        hitSlop={6}
        onPress={() => onChange(value - 1)}
        style={[
          isLight ? lightStyles.stepperButton : styles.stepperButton,
          value <= min && styles.dim,
        ]}
      >
        <Glyph
          d="M5 12h14"
          color={isLight ? light.secondary : undefined}
          size={isLight ? 12 : 14}
        />
      </Pressable>
      <Text style={isLight ? lightStyles.stepperValue : styles.stepperValue}>
        {value}
      </Text>
      <Pressable
        accessibilityLabel="More"
        accessibilityRole="button"
        disabled={disabled || value >= max}
        hitSlop={6}
        onPress={() => onChange(value + 1)}
        style={[
          isLight ? lightStyles.stepperButton : styles.stepperButton,
          (disabled || value >= max) && styles.dim,
        ]}
      >
        <Glyph
          d="M12 5v14M5 12h14"
          color={isLight ? light.primary : undefined}
          size={isLight ? 12 : 14}
        />
      </Pressable>
    </View>
  );
}

export function EventTierCard({
  tier: listed,
  now,
  quantity,
  room = Infinity,
  busy,
  onQuantity,
  onWaitlist,
  waitlistNeedsEmail = true,
  phoneRule = "off",
  variant = "dark",
}: {
  tier: EventTier;
  now: number;
  quantity: number;
  /** How many more tickets the event allows in this order. */
  room?: number;
  busy?: boolean;
  onQuantity(next: number): void;
  onWaitlist(
    name: string,
    email: string,
    request?: { phone?: string; quantity?: number },
  ): void;
  /** Signed-in hosts already know the email. */
  waitlistNeedsEmail?: boolean;
  /** Whether a request for this event's approval tiers asks for a phone. */
  phoneRule?: EventDetailRule;
  variant?: EventTicketsVariant;
}) {
  const isLight = variant === "light";
  const s = isLight ? lightStyles : styles;
  const placeholderColor = isLight ? light.secondary : recipe.colors.white50;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [places, setPlaces] = useState(1);
  const tier = tierForFan(listed);
  const availability = tierAvailability(tier, now);
  const available = availability === "available";
  // A tier that takes requests is asked for, never put in the basket.
  const requested = !!tier.requiresApproval && available;
  const asksPhone = requested && phoneRule !== "off";
  const note = tierLabel(
    tier,
    availability === "sold-out"
      ? "Sold out"
      : availability === "not-yet"
        ? tier.saleStartsAt && !tier.saleAwaitingTrigger
          ? `On sale from ${formatWhen(tier.saleStartsAt)}`
          : "Not on sale yet"
        : availability === "ended"
          ? "Sales ended"
          : requested
            ? "Approval required"
            : tier.saleEndsAt
              ? `Sales end ${formatWhen(tier.saleEndsAt)}`
              : null,
  );
  const canJoin =
    !!name.trim() &&
    (!waitlistNeedsEmail || !!email) &&
    (!asksPhone || phoneReady(phoneRule, phone));
  // Light: a free tier is offered as a link until one is picked; then it
  // gets the stepper like any other tier (back to zero brings the link back).
  const offerRegister =
    isLight &&
    tier.priceMinor === 0 &&
    quantity === 0 &&
    !tier.soldOut &&
    !requested;
  return (
    <View style={[s.card, !available && !tier.soldOut && styles.dim]}>
      <View style={styles.cardRow}>
        <View style={s.cardCopy}>
          <Text style={s.cardTitle}>{tier.name}</Text>
          <Text style={s.cardPrice}>
            {tier.priceMinor === 0 && !isLight
              ? "Free"
              : formatMoney(tier.priceMinor, tier.currency, tier.minorUnits)}
            {/* A fan club member's price: the list price struck through. */}
            {tier.listPriceMinor !== undefined ? (
              <>
                {" "}
                <Text
                  style={styles.listPrice}
                  accessibilityLabel={`was ${formatMoney(tier.listPriceMinor, tier.currency, tier.minorUnits)}`}
                >
                  {formatMoney(tier.listPriceMinor, tier.currency, tier.minorUnits)}
                </Text>
                {` · ${MEMBER_PRICE}`}
              </>
            ) : null}
          </Text>
          {note ? <Text style={s.cardNote}>{note}</Text> : null}
        </View>
        {offerRegister ? (
          <Pressable
            accessibilityLabel={`Register for ${tier.name}`}
            accessibilityRole="button"
            disabled={busy || !available || room < 1}
            hitSlop={8}
            onPress={() => onQuantity(1)}
          >
            <Text style={lightStyles.link}>Register</Text>
          </Pressable>
        ) : !tier.soldOut && !requested ? (
          <QuantityStepper
            value={quantity}
            max={Math.min(tierMax(tier), quantity + room)}
            disabled={busy || !available}
            onChange={onQuantity}
            variant={variant}
          />
        ) : null}
      </View>
      {/* A members' presale has no waitlist (LIS-1156). */}
      {(tier.soldOut && !tier.gated) || requested ? (
        <View style={s.waitlist}>
          <TextInput
            accessibilityLabel="Name"
            placeholder="Your name"
            placeholderTextColor={placeholderColor}
            style={s.input}
            value={name}
            onChangeText={setName}
          />
          {waitlistNeedsEmail ? (
            <TextInput
              accessibilityLabel="Email"
              autoCapitalize="none"
              keyboardType="email-address"
              placeholder="Email"
              placeholderTextColor={placeholderColor}
              style={s.input}
              value={email}
              onChangeText={setEmail}
            />
          ) : (
            <Text style={s.cardNote}>
              {requested
                ? "The answer goes to your signed-in email."
                : "Invitations go to your signed-in email."}
            </Text>
          )}
          {asksPhone ? (
            <TextInput
              accessibilityLabel="Phone number"
              keyboardType="phone-pad"
              placeholder={
                phoneRule === "required"
                  ? "Phone number"
                  : "Phone number (optional)"
              }
              placeholderTextColor={placeholderColor}
              style={s.input}
              value={phone}
              onChangeText={setPhone}
            />
          ) : null}
          {requested && tierMax(tier) > 1 ? (
            <View style={styles.cardRow}>
              <Text style={[s.cardNote, { flex: 1 }]}>How many places</Text>
              <QuantityStepper
                value={places}
                min={1}
                max={tierMax(tier)}
                disabled={busy}
                onChange={setPlaces}
                variant={variant}
              />
            </View>
          ) : null}
          <Pressable
            accessibilityRole="button"
            disabled={busy || !canJoin}
            onPress={() =>
              onWaitlist(
                name.trim(),
                email,
                requested
                  ? {
                      ...(asksPhone && phone.trim()
                        ? { phone: phone.trim() }
                        : {}),
                      ...(places > 1 ? { quantity: places } : {}),
                    }
                  : undefined,
              )
            }
            style={[s.secondaryButton, !canJoin && styles.dim]}
          >
            <Text style={s.secondaryButtonText}>Join waitlist</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

export function EventTicketsScreen({
  tiers,
  now,
  quantities,
  busy,
  message,
  lines,
  onQuantity,
  onWaitlist,
  onContinue,
  continueLabel = "Continue",
  waitlistNeedsEmail,
  orderLimits,
  footer,
  variant = "dark",
  listMaxHeight,
  hostButton = false,
  buyerDetails,
  goods,
}: {
  /** What is sold: an event's tickets (absent), or a merch product's
   *  sizes, named as such (`goodsWords`). */
  goods?: Goods;
  tiers: EventTier[];
  now: number;
  quantities: Record<string, number>;
  busy?: boolean;
  message?: string;
  lines: EventLine[];
  onQuantity(itemId: string, next: number): void;
  onWaitlist(
    itemId: string,
    name: string,
    email: string,
    request?: { phone?: string; quantity?: number },
  ): void;
  /** What the event asks of a fan beyond their email, and what they typed.
   *  Given, the fields show under the tiers once something is selected. */
  buyerDetails?: {
    rules: EventDetailRules;
    name: string;
    phone: string;
    onName(value: string): void;
    onPhone(value: string): void;
  };
  onContinue(): void;
  /** The button's label; the host says what continuing opens. */
  continueLabel?: string;
  waitlistNeedsEmail?: boolean;
  /** The event's per-order ticket limits across all its tiers (LIS-924):
   *  the steppers stop at the maximum, and a hint says what is missing
   *  below the minimum. A host drawing its own button disables it too. */
  orderLimits?: EventOrderLimits | null;
  /** Host slot under the button (safe-area spacing). */
  footer?: ReactNode;
  variant?: EventTicketsVariant;
  /** Scroll the tier list inside this height so the button stays in view
   *  (sheets sized to their content). */
  listMaxHeight?: number;
  /** The host draws the button (one button through the tickets and the
   *  checkout); `onContinue` and `continueLabel` are then unused here. */
  hostButton?: boolean;
}) {
  const isLight = variant === "light";
  const s = isLight ? lightStyles : styles;
  const limits = orderLimitState(orderLimits, lines);
  const list = (
    <View style={s.stack}>
      {tiers.length === 0 ? (
        <Text style={s.message}>{goodsWords(goods).none}</Text>
      ) : null}
      {tiers.map((tier) => (
        <EventTierCard
          key={tier.engineItemId}
          tier={tier}
          now={now}
          quantity={quantities[tier.engineItemId] ?? 0}
          room={limits.room}
          busy={busy}
          onQuantity={(next) => onQuantity(tier.engineItemId, next)}
          onWaitlist={(name, email, request) =>
            onWaitlist(tier.engineItemId, name, email, request)
          }
          waitlistNeedsEmail={waitlistNeedsEmail}
          phoneRule={buyerDetails?.rules.phone ?? "off"}
          variant={variant}
        />
      ))}
      {buyerDetails && lines.length > 0 ? (
        <BuyerDetailFields {...buyerDetails} busy={busy} variant={variant} />
      ) : null}
      {limits.problem ? (
        <Text accessibilityLiveRegion="polite" style={s.message}>
          {limits.problem}
        </Text>
      ) : null}
      {message ? <Text style={s.message}>{message}</Text> : null}
    </View>
  );
  return (
    <View style={s.screen}>
      {listMaxHeight ? (
        <ScrollView
          style={{ maxHeight: listMaxHeight, flexGrow: 0 }}
          keyboardShouldPersistTaps="handled"
        >
          {list}
        </ScrollView>
      ) : (
        list
      )}
      {hostButton ? null : (
        <Cta
          disabled={
            !lines.length ||
            !!limits.problem ||
            busy ||
            (!!buyerDetails && !detailsReady(buyerDetails.rules, buyerDetails))
          }
          label={continueLabel}
          onPress={onContinue}
          variant={variant}
        />
      )}
      {footer}
    </View>
  );
}

/** The details an event asks for at checkout, under the tiers. */
function BuyerDetailFields({
  rules,
  name,
  phone,
  onName,
  onPhone,
  busy,
  variant,
}: {
  rules: EventDetailRules;
  name: string;
  phone: string;
  onName(value: string): void;
  onPhone(value: string): void;
  busy?: boolean;
  variant: EventTicketsVariant;
}) {
  if (rules.name === "off" && rules.phone === "off") return null;
  const isLight = variant === "light";
  const s = isLight ? lightStyles : styles;
  const placeholderColor = isLight ? light.secondary : recipe.colors.white50;
  return (
    <View style={{ gap: 8 }}>
      {rules.name === "off" ? null : (
        <TextInput
          accessibilityLabel="Your name"
          autoComplete="name"
          editable={!busy}
          placeholder={
            rules.name === "required" ? "Your name" : "Your name (optional)"
          }
          placeholderTextColor={placeholderColor}
          style={s.input}
          value={name}
          onChangeText={onName}
        />
      )}
      {rules.phone === "off" ? null : (
        <TextInput
          accessibilityLabel="Phone number"
          autoComplete="tel"
          editable={!busy}
          keyboardType="phone-pad"
          placeholder={
            rules.phone === "required"
              ? "Phone number"
              : "Phone number (optional)"
          }
          placeholderTextColor={placeholderColor}
          style={s.input}
          value={phone}
          onChangeText={onPhone}
        />
      )}
    </View>
  );
}

function Cta({
  label,
  disabled,
  onPress,
  style,
  variant = "dark",
}: {
  label: string;
  disabled?: boolean;
  onPress(): void;
  style?: ViewStyle;
  variant?: EventTicketsVariant;
}) {
  const s = variant === "light" ? lightStyles : styles;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[s.cta, disabled && styles.dim, style]}
    >
      <Text style={s.ctaText}>{label}</Text>
    </Pressable>
  );
}

const g = recipe.geometry;
const styles = StyleSheet.create({
  screen: { flex: 1, justifyContent: "space-between", gap: 20 },
  stack: { gap: g.gap },
  card: {
    padding: 16,
    borderRadius: g.cardRadius,
    backgroundColor: recipe.colors.white10,
  },
  cardRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  cardCopy: { flex: 1, gap: 6 },
  cardTitle: { color: recipe.colors.white, fontSize: 15, fontWeight: "700" },
  cardPrice: { color: recipe.colors.white50, fontSize: 14 },
  cardNote: { color: recipe.colors.white70, fontSize: 12 },
  dim: { opacity: 0.5 },
  listPrice: { textDecorationLine: "line-through" },
  stepper: { flexDirection: "row", alignItems: "center", gap: 10 },
  stepperButton: {
    width: 32,
    height: 32,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: recipe.colors.white10,
  },
  stepperValue: {
    minWidth: 18,
    textAlign: "center",
    color: recipe.colors.white,
    fontSize: 15,
    fontWeight: "700",
  },
  waitlist: {
    gap: 8,
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: recipe.colors.border,
  },
  input: {
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: recipe.colors.white20,
    borderRadius: g.controlRadius,
    color: recipe.colors.white,
    fontSize: 13,
  },
  secondaryButton: {
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: recipe.colors.white10,
    borderRadius: g.controlRadius,
  },
  secondaryButtonText: {
    color: recipe.colors.white,
    fontSize: 12,
    fontWeight: "600",
  },
  message: { color: recipe.colors.white80, fontSize: 13, textAlign: "center" },
  cta: {
    minHeight: 50,
    paddingHorizontal: 18,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: recipe.colors.white,
  },
  ctaText: {
    color: recipe.colors.black,
    fontSize: 13,
    fontWeight: "700",
    textTransform: "uppercase",
  },
});

const lightStyles = StyleSheet.create({
  screen: { gap: 16 },
  stack: { gap: 8 },
  card: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: light.card,
  },
  cardCopy: { flex: 1, gap: 2 },
  cardTitle: { color: light.text, fontSize: 15, fontWeight: "600" },
  cardPrice: { color: light.secondary, fontSize: 13 },
  cardNote: { color: light.secondary, fontSize: 12 },
  stepperButton: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: light.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: light.border,
  },
  stepperValue: {
    minWidth: 18,
    textAlign: "center",
    color: light.text,
    fontSize: 15,
    fontWeight: "600",
  },
  link: { color: light.primary, fontSize: 14, fontWeight: "500" },
  waitlist: {
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: light.border,
  },
  input: {
    height: 40,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: light.border,
    borderRadius: 10,
    backgroundColor: light.surface,
    color: light.text,
    fontSize: 14,
  },
  secondaryButton: {
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: light.border,
    borderRadius: 10,
    backgroundColor: light.surface,
  },
  secondaryButtonText: {
    color: light.primary,
    fontSize: 14,
    fontWeight: "600",
  },
  message: { color: light.secondary, fontSize: 13, textAlign: "center" },
  cta: {
    minHeight: 50,
    paddingHorizontal: 18,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: light.primary,
  },
  ctaText: { color: light.onPrimary, fontSize: 16, fontWeight: "600" },
});
